import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Brain,
  PencilSimple,
  Plus,
  Check,
  Trash,
  Spinner,
  WarningCircle,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { api } from "../api";

// ── Constants ─────────────────────────────────────────────────────────────────

const SKILL_LABELS = {
  meaning:     "Meaning",
  sentence:    "Sentence Completion",
  context:     "Context",
  odd_one_out: "Odd One Out",
};

const SKILL_ORDER = ["meaning", "sentence", "context", "odd_one_out"];

const SKILL_VARIANT = {
  meaning:     "primary",
  sentence:    "teal",
  context:     "blue",
  odd_one_out: "warning",
};

// ── QuestionCard ──────────────────────────────────────────────────────────────

function QuestionCard({ question, wordId, onUpdated, onDeleted }) {
  const [editing, setEditing]   = useState(false);
  const [saving,  setSaving]    = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    prompt:       question.prompt,
    options:      [...question.options],
    correct_text: question.correct_text,
  });

  // Keep form in sync if parent passes updated question (e.g. after regen)
  useEffect(() => {
    setForm({
      prompt:       question.prompt,
      options:      [...question.options],
      correct_text: question.correct_text,
    });
  }, [question]);

  function updateOption(idx, value) {
    const newOpts    = [...form.options];
    const wasCorrect = form.correct_text === form.options[idx];
    newOpts[idx]     = value;
    setForm((f) => ({
      ...f,
      options:      newOpts,
      correct_text: wasCorrect ? value : f.correct_text,
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api(`/admin/quiz-bank/${wordId}/questions/${question.question_id}`, {
        method: "PUT",
        body:   form,
      });
      setEditing(false);
      onUpdated({ ...question, ...form });
    } catch (err) {
      alert(err?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm("Delete this question?")) return;
    setDeleting(true);
    try {
      await api(`/admin/quiz-bank/${wordId}/questions/${question.question_id}`, {
        method: "DELETE",
      });
      onDeleted(question.question_id);
    } catch (err) {
      alert(err?.message ?? "Delete failed");
      setDeleting(false);
    }
  }

  // ── Edit mode ──────────────────────────────────────────────────────────────
  if (editing) {
    return (
      <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-4">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-primary-strong">
          Editing {question.question_id}
        </p>
        <textarea
          value={form.prompt}
          onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))}
          rows={3}
          className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        <div className="mt-3 space-y-2">
          {form.options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              {/* Correct-answer toggle */}
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, correct_text: opt || f.correct_text }))}
                className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 transition ${
                  form.correct_text === opt
                    ? "border-success bg-success"
                    : "border-border hover:border-success/60"
                }`}
                title="Mark as correct answer"
              >
                {form.correct_text === opt && (
                  <Check size={11} weight="bold" className="text-white" />
                )}
              </button>
              <span className="w-5 text-xs font-bold text-muted-foreground">
                {String.fromCharCode(65 + i)}.
              </span>
              <input
                value={opt}
                onChange={(e) => updateOption(i, e.target.value)}
                className="flex-1 rounded-xl border border-border bg-background px-3 py-1.5 text-sm font-semibold text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <BloomButton size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size={14} className="animate-spin" /> : <Check size={14} weight="bold" />}
            {saving ? "Saving…" : "Save"}
          </BloomButton>
          <BloomButton variant="outline" size="sm" onClick={() => setEditing(false)}>
            Cancel
          </BloomButton>
        </div>
      </div>
    );
  }

  // ── View mode ──────────────────────────────────────────────────────────────
  return (
    <div className="group rounded-2xl bg-muted/40 p-4 transition hover:bg-muted/60">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <span className="mt-0.5 flex-shrink-0 rounded-lg bg-muted px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-muted-foreground">
            {question.question_id}
          </span>
          <p className="text-sm font-semibold text-foreground leading-snug">{question.prompt}</p>
        </div>
        <div className="flex flex-shrink-0 gap-1 opacity-0 transition group-hover:opacity-100">
          <button
            onClick={() => setEditing(true)}
            className="flex h-7 w-7 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition hover:border-primary/40 hover:text-primary"
            title="Edit question"
          >
            <PencilSimple size={13} weight="bold" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex h-7 w-7 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition hover:border-destructive/40 hover:text-destructive"
            title="Delete question"
          >
            {deleting ? <Spinner size={13} className="animate-spin" /> : <Trash size={13} weight="bold" />}
          </button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        {question.options.map((opt, i) => (
          <div
            key={i}
            className={`rounded-xl px-3 py-2 text-xs font-semibold ${
              opt === question.correct_text
                ? "bg-success/15 font-extrabold text-success-ink"
                : "bg-background text-muted-foreground"
            }`}
          >
            {String.fromCharCode(65 + i)}. {opt}
            {opt === question.correct_text && " ✓"}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── AddQuestionForm ───────────────────────────────────────────────────────────

function AddQuestionForm({ wordId, defaultSkill, onAdded, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    skill:        defaultSkill || "meaning",
    prompt:       "",
    options:      ["", "", "", ""],
    correct_text: "",
  });

  function updateOption(idx, value) {
    const newOpts    = [...form.options];
    const wasCorrect = form.correct_text === form.options[idx];
    newOpts[idx]     = value;
    setForm((f) => ({
      ...f,
      options:      newOpts,
      correct_text: wasCorrect ? value : f.correct_text,
    }));
  }

  async function handleSave() {
    if (!form.prompt.trim()) return alert("Please enter a question prompt.");
    if (form.options.some((o) => !o.trim())) return alert("Please fill in all 4 options.");
    if (!form.correct_text) return alert("Please mark one option as correct.");
    setSaving(true);
    try {
      const result = await api(`/admin/quiz-bank/${wordId}/questions`, {
        method: "POST",
        body:   form,
      });
      onAdded({ ...form, question_id: result.question_id });
    } catch (err) {
      alert(err?.message ?? "Failed to add question");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-2xl border-2 border-dashed border-primary/30 bg-primary/3 p-4">
      <div className="mb-3 flex items-center gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-primary-strong">New Question</p>
        <select
          value={form.skill}
          onChange={(e) => setForm((f) => ({ ...f, skill: e.target.value }))}
          className="ml-auto rounded-xl border border-border bg-background px-2.5 py-1 text-xs font-semibold text-foreground focus:border-primary focus:outline-none"
        >
          {SKILL_ORDER.map((s) => (
            <option key={s} value={s}>{SKILL_LABELS[s]}</option>
          ))}
        </select>
      </div>
      <textarea
        value={form.prompt}
        onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))}
        placeholder="Question text / sentence with ______ blank…"
        rows={3}
        className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
      <div className="mt-3 space-y-2">
        {form.options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, correct_text: opt || f.correct_text }))}
              className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 transition ${
                form.correct_text === opt && opt
                  ? "border-success bg-success"
                  : "border-border hover:border-success/60"
              }`}
              title="Mark as correct"
            >
              {form.correct_text === opt && opt && (
                <Check size={11} weight="bold" className="text-white" />
              )}
            </button>
            <span className="w-5 text-xs font-bold text-muted-foreground">
              {String.fromCharCode(65 + i)}.
            </span>
            <input
              value={opt}
              onChange={(e) => updateOption(i, e.target.value)}
              placeholder={`Option ${String.fromCharCode(65 + i)}`}
              className="flex-1 rounded-xl border border-border bg-background px-3 py-1.5 text-sm font-semibold text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <BloomButton size="sm" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
          {saving ? "Adding…" : "Add Question"}
        </BloomButton>
        <BloomButton variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </BloomButton>
      </div>
    </div>
  );
}

// ── SkillSection ──────────────────────────────────────────────────────────────

function SkillSection({ skill, questions, wordId, onQuestionsChange }) {
  const [addingHere, setAddingHere] = useState(false);

  function handleUpdated(updated) {
    onQuestionsChange(
      questions.map((q) => (q.question_id === updated.question_id ? updated : q))
    );
  }

  function handleDeleted(questionId) {
    onQuestionsChange(questions.filter((q) => q.question_id !== questionId));
  }

  function handleAdded(newQ) {
    onQuestionsChange([...questions, newQ]);
    setAddingHere(false);
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <BloomBadge variant={SKILL_VARIANT[skill]} className="px-2.5 py-1 text-xs">
          {SKILL_LABELS[skill]}
        </BloomBadge>
        <span className="text-xs font-semibold text-muted-foreground">
          {questions.length} question{questions.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="space-y-2">
        {questions.length === 0 && (
          <p className="rounded-2xl bg-muted/40 px-4 py-3 text-xs font-semibold text-muted-foreground">
            No {SKILL_LABELS[skill].toLowerCase()} questions yet.
          </p>
        )}
        {questions.map((q) => (
          <QuestionCard
            key={q.question_id}
            question={q}
            wordId={wordId}
            onUpdated={handleUpdated}
            onDeleted={handleDeleted}
          />
        ))}
        {addingHere ? (
          <AddQuestionForm
            wordId={wordId}
            defaultSkill={skill}
            onAdded={handleAdded}
            onCancel={() => setAddingHere(false)}
          />
        ) : (
          <button
            onClick={() => setAddingHere(true)}
            className="flex w-full items-center gap-2 rounded-2xl border border-dashed border-border px-4 py-2.5 text-xs font-semibold text-muted-foreground transition hover:border-primary/40 hover:text-primary"
          >
            <Plus size={13} weight="bold" />
            Add {SKILL_LABELS[skill]} question
          </button>
        )}
      </div>
    </div>
  );
}

// ── QuizBankModal (main export) ───────────────────────────────────────────────

/**
 * Props:
 *   word       — word object from the words list (must have word_id, word)
 *   onClose    — called when the panel is dismissed
 *   onGenerated — called after a fresh generation (so AdminWords can update bankIds)
 */
export function QuizBankModal({ word, onClose, onGenerated }) {
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [questions,    setQuestions]    = useState([]);   // flat array
  const [regenerating, setRegenerating] = useState(false);

  const fetchBank = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api(`/admin/quiz-bank/${word.word_id}`);
      setQuestions(data.questions ?? []);
    } catch (err) {
      setError(err?.message ?? "Failed to load quiz bank");
    } finally {
      setLoading(false);
    }
  }, [word.word_id]);

  useEffect(() => { fetchBank(); }, [fetchBank]);

  async function handleRegenerate() {
    if (!window.confirm(`Regenerate all questions for "${word.word}"? This will replace the existing bank.`)) return;
    setRegenerating(true);
    try {
      await api("/admin/quiz-bank/generate", {
        method: "POST",
        body:   { words: word.word },
      });
      await fetchBank();
      onGenerated?.();
    } catch (err) {
      alert(err?.message ?? "Regeneration failed");
    } finally {
      setRegenerating(false);
    }
  }

  // Group questions by skill
  const grouped = SKILL_ORDER.reduce((acc, skill) => {
    acc[skill] = questions.filter((q) => q.skill === skill);
    return acc;
  }, {});

  function handleSectionChange(skill, updatedSectionQs) {
    setQuestions((prev) => [
      ...prev.filter((q) => q.skill !== skill),
      ...updatedSectionQs,
    ]);
  }

  const totalCount = questions.length;

  return (
    <AnimatePresence>
      <motion.div
        key="quiz-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        key="quiz-panel"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/12 text-primary-strong">
              <Brain size={18} weight="bold" />
            </span>
            <div>
              <h2 className="font-display text-lg font-black capitalize text-foreground">
                {word.word}
              </h2>
              <p className="text-xs font-semibold text-muted-foreground">
                Quiz Bank
                {!loading && ` · ${totalCount} question${totalCount !== 1 ? "s" : ""}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <BloomButton
              variant="outline"
              size="sm"
              onClick={handleRegenerate}
              disabled={regenerating || loading}
            >
              {regenerating ? (
                <Spinner size={14} className="animate-spin" />
              ) : (
                <ArrowCounterClockwise size={14} weight="bold" />
              )}
              {regenerating ? "Regenerating…" : "Regenerate All"}
            </BloomButton>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-xl border border-border text-muted-foreground transition hover:border-destructive/40 hover:text-destructive"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Spinner size={28} className="animate-spin text-primary" />
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <WarningCircle size={36} className="text-destructive" weight="fill" />
              <p className="text-sm font-semibold text-destructive">{error}</p>
              <BloomButton variant="outline" size="sm" onClick={fetchBank}>
                Retry
              </BloomButton>
            </div>
          )}

          {!loading && !error && totalCount === 0 && (
            <div className="flex flex-col items-center gap-4 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-muted">
                <Brain size={30} className="text-muted-foreground" />
              </span>
              <div>
                <p className="font-display text-base font-black text-foreground">No quiz bank yet</p>
                <p className="mt-1 text-sm font-semibold text-muted-foreground">
                  Generate AI questions for "{word.word}" to get started.
                </p>
              </div>
              <BloomButton onClick={handleRegenerate} disabled={regenerating}>
                {regenerating ? (
                  <><Spinner size={15} className="animate-spin" /> Generating…</>
                ) : (
                  <><Brain size={15} weight="bold" /> Generate Quiz Bank</>
                )}
              </BloomButton>
            </div>
          )}

          {!loading && !error && totalCount > 0 && (
            <div className="space-y-8">
              {SKILL_ORDER.map((skill) => (
                <SkillSection
                  key={skill}
                  skill={skill}
                  questions={grouped[skill]}
                  wordId={word.word_id}
                  onQuestionsChange={(updated) => handleSectionChange(skill, updated)}
                />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
